import { createContext } from 'react';
import { useInitContext } from '../init/context';

export interface SessionContextState {
  account: { access: MyRecord<string[]> | '*' } | null;
}

export type SessionContextValue = ReturnType<typeof useSessionContextValue>;

export const sessionContext = createContext<SessionContextValue>({} as never);

export function getInitState(
  defaultState?: Partial<SessionContextState>
): SessionContextState {
  const state: SessionContextState = {
    account: null,
  };

  if (defaultState) {
    for (const k in defaultState) {
      const v = defaultState[k as keyof SessionContextState];
      if (v !== undefined) {
        (state as any)[k] = v;
      }
    }
  }

  return state;
}

export function useSessionContextValue() {
  const { pushInit } = useInitContext();
  const [session, setSession] = useState(getInitState());
  const [loading, setLoading] = useState(false);

  const initSession = useCallback(async () => {
    setLoading(true);
    try {
      setSession(getInitState({}));
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async () => {
    setLoading(true);
    try {
      await initSession();
    } finally {
      setLoading(false);
    }
  }, [initSession]);

  const logout = useCallback(async () => {
    setLoading(true);
    try {
      /* empty */
    } finally {
      setSession(getInitState());
      setLoading(false);
    }
  }, []);

  const value = useMemo(
    () => ({
      session,
      setSession,
      initSession,
      loading,
      setLoading,
      login,
      logout,
    }),
    [session, initSession, loading, login, logout]
  );

  useLayoutEffect(() => {
    pushInit(initSession());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return value;
}

export function useSessionContext() {
  return useContext(sessionContext);
}
